import { Bot } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Alert, Button, EmptyState, Skeleton } from '@/components/ui'

import { useAgents } from '../queries'
import { AgentCard } from './AgentCard'
import { CreateAgentForm } from './CreateAgentForm'

export interface AgentsListProps {
  readonly tenantId: string
}

/**
 * Lista os agentes do tenant em cards, com o formulário de criação no topo. Estados: carregando
 * (skeleton), erro (Alert + tentar de novo), vazio (EmptyState) e a lista de cards. Com um único
 * agente, o card já abre o teste.
 */
export function AgentsList({ tenantId }: AgentsListProps) {
  const { t } = useTranslation()
  const agentsQuery = useAgents(tenantId)

  if (agentsQuery.isPending) {
    return (
      <div
        className="flex flex-col gap-4"
        role="status"
        aria-busy="true"
        aria-label={t('agents.loading')}
      >
        {Array.from({ length: 2 }, (_, index) => (
          <Skeleton key={index} className="h-40 w-full rounded-lg" />
        ))}
      </div>
    )
  }

  if (agentsQuery.isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Alert variant="danger" title={t('agents.loadError')} />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void agentsQuery.refetch()
          }}
        >
          {t('retry')}
        </Button>
      </div>
    )
  }

  const single = agentsQuery.data.length === 1

  return (
    <div className="flex flex-col gap-4">
      <CreateAgentForm tenantId={tenantId} />

      {agentsQuery.data.length === 0 ? (
        <EmptyState
          icon={Bot}
          title={t('agents.empty.title')}
          description={t('agents.empty.description')}
        />
      ) : (
        agentsQuery.data.map((agent) => (
          <AgentCard key={agent.id} tenantId={tenantId} agent={agent} defaultTestOpen={single} />
        ))
      )}
    </div>
  )
}
